/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/inspector.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                          |
 * |---|------------------------|-------------------|------------------|---------------------------------------|
 * | 1 | 2026-06-18 (Phase 3c)  | Claude Sonnet 4.6 | Inspect Mode     | Created — InspectorV0 class           |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (InspectorV0, InspectResult, InspectListener)
 *
 * EXPORTS
 * -------
 * InspectResult   — { node, tree, flat, ancestors, deviceX, deviceY }
 * InspectListener — (result: InspectResult | null) => void
 * InspectorV0     — canvas pointer listener: hit-test only, no tap injection
 *
 * CALLER → CALLEE MAP
 * --------------------
 * App.tsx → inspectorRef.current.start(adb, canvas)
 *   pointerdown → #handleClick
 *     → mapToDeviceCoords
 *     → dumpTree(adb)
 *     → hitTestTree(tree, x, y)
 *     → flattenTree, buildParentMap, getAncestors
 *     → notify listeners with InspectResult
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. InspectorV0 NEVER calls injectTap. It is purely perceptual — inspect mode
 *    reads the tree and reports what was hit; nothing is sent to the device.
 * 2. The full tree (roots + flat + ancestors) is included in InspectResult so
 *    InspectPanel can show context (parent path, siblings, full attribute set)
 *    without a second dumpTree call.
 * 3. dumpTree is injectable for unit tests (same pattern as RecorderV0).
 * 4. If dumpTree fails, the listener is called with null so the panel can show
 *    a "Tree dump failed" message rather than silently hanging.
 * 5. The InspectorV0 does not manage its own loading state — the caller
 *    (App.tsx) sets `inspecting: true` around the async dumpTree call via
 *    the onDumpStart / onDumpEnd callbacks in InspectorDeps.
 *
 * =============================================================================
 */

import type { Adb } from "@yume-chan/adb";
import type { A11yNode } from "./treeParser.js";
import {
  mapToDeviceCoords,
  hitTestTree,
  flattenTree,
  buildParentMap,
  getAncestors,
} from "./recorder.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InspectResult {
  node: A11yNode;
  tree: A11yNode[];        // root nodes (for highlight coordinate normalisation)
  flat: A11yNode[];        // pre-order flattened tree
  ancestors: A11yNode[];  // nearest-first ancestor chain (up to depth 4)
  deviceX: number;        // tapped device pixel X
  deviceY: number;        // tapped device pixel Y
}

export type InspectListener = (result: InspectResult | null) => void;

export interface InspectorDeps {
  dumpTree?: (adb: Adb) => Promise<A11yNode[]>;
  onDumpStart?: () => void;   // called before dumpTree (for loading spinner)
  onDumpEnd?: () => void;     // called after dumpTree completes or fails
}

// ── InspectorV0 ───────────────────────────────────────────────────────────────

export class InspectorV0 {
  #adb: Adb | null = null;
  #canvas: HTMLCanvasElement | null = null;
  #listeners = new Set<InspectListener>();
  readonly #dumpTree: (adb: Adb) => Promise<A11yNode[]>;
  readonly #onDumpStart: (() => void) | null;
  readonly #onDumpEnd: (() => void) | null;

  readonly #onPointerDown: (e: Event) => void = (e: Event) => {
    void this.#handleClick(e as PointerEvent);
  };

  constructor(deps: InspectorDeps = {}) {
    this.#dumpTree =
      deps.dumpTree ??
      ((adb) => import("./treeParser.js").then((m) => m.dumpTree(adb)));
    this.#onDumpStart = deps.onDumpStart ?? null;
    this.#onDumpEnd = deps.onDumpEnd ?? null;
  }

  /** Attach to a canvas. Any previous attachment is replaced. */
  start(adb: Adb, canvas: HTMLCanvasElement): void {
    this.#detach();
    this.#adb = adb;
    this.#canvas = canvas;
    canvas.addEventListener("pointerdown", this.#onPointerDown);
  }

  /** Detach from canvas. */
  stop(): void {
    this.#detach();
  }

  subscribe(listener: InspectListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #detach(): void {
    this.#canvas?.removeEventListener("pointerdown", this.#onPointerDown);
    this.#adb = null;
    this.#canvas = null;
  }

  #notify(result: InspectResult | null): void {
    for (const listener of this.#listeners) listener(result);
  }

  async #handleClick(e: PointerEvent): Promise<void> {
    const adb = this.#adb;
    const canvas = this.#canvas;
    if (!adb || !canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    const [deviceX, deviceY] = mapToDeviceCoords(
      cssX, cssY, rect.width, rect.height, canvas.width, canvas.height,
    );

    this.#onDumpStart?.();
    try {
      const tree = await this.#dumpTree(adb);
      const flat = flattenTree(tree);
      const parentMap = buildParentMap(tree);
      const node = hitTestTree(tree, deviceX, deviceY);

      if (!node) {
        this.#notify(null);
        return;
      }

      const ancestors = getAncestors(node, parentMap);
      this.#notify({ node, tree, flat, ancestors, deviceX, deviceY });
    } catch (err) {
      console.error("[Inspector] dumpTree failed:", err);
      this.#notify(null);
    } finally {
      this.#onDumpEnd?.();
    }
  }
}
